import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  RgbaColor,
  selectEnvironmentInteractionPoints,
} from "../../app/slices/EnvironmentSlice";
import {
  closeSequencePositionPicker,
  selectSequencePositionPicker,
  selectSelectedTab,
  SelectedTab,
} from "../../app/slices/GlobalSlice";
import {
  selectActors,
  selectSelectedElement,
  selectSelectedElementType,
  selectSelectedTake3D,
} from "../../app/slices/TrainingSlice";
import useSituationHelper from "../../hooks/SituationHelper.hook";
import { Actor } from "../../models/Actor";
import { IEmotionBubble } from "../../models/interactions/EmotionBubble";
import Interaction, {
  InteractionType,
} from "../../models/interactions/Interaction";
import { ILookat } from "../../models/interactions/Lookat";
import { IMessageBubble } from "../../models/interactions/MessageBubble";
import { ISequence } from "../../models/interactions/Sequence";
import useUpdateSequenceInteraction from "../sequence/node_previews/UpdateSequenceInteraction.hook";
import InteractionPointPreview, {
  ActorPointStrategy,
  CameraPointStrategy,
  NpcPointStrategy,
  PlayerPointStrategy,
  PropPointStrategy,
  SelectedCameraPointStrategy,
} from "./InteractionPointPreview";
import {
  getActorPositionIdInTake,
  getEnvironmentPositionContexts,
  getSituationPositionId,
} from "../sequence/EnvironmentPositionContext";

const selectedMarkerColor: RgbaColor = {
  r: 58 / 255,
  g: 196 / 255,
  b: 1,
  a: 1,
};

function applyPickedTargetToLookat(
  interaction: ILookat,
  targetIndex: number,
  targetId: string,
): ILookat {
  const lookats = interaction.lookats.length
    ? [...interaction.lookats]
    : [{ sourceNpcId: "", targetId: "" }];
  const index = Math.max(0, Math.min(targetIndex, lookats.length - 1));

  lookats[index] = {
    ...(lookats[index] ?? { sourceNpcId: "", targetId: "" }),
    targetId,
  };

  return {
    ...interaction,
    lookats,
  };
}

function applyPickedTargetToInteraction(
  interaction: Interaction,
  targetIndex: number,
  targetId: string,
): Interaction {
  if (interaction.type === InteractionType.Lookat) {
    return applyPickedTargetToLookat(
      interaction as ILookat,
      targetIndex,
      targetId,
    );
  }

  if (interaction.type === InteractionType.MessageBubble) {
    return {
      ...(interaction as IMessageBubble),
      lookAt: targetId,
    } as IMessageBubble;
  }

  if (interaction.type === InteractionType.EmotionBubble) {
    return {
      ...(interaction as IEmotionBubble),
      lookAt: targetId,
    } as IEmotionBubble;
  }

  return interaction;
}

function getSelectedTargetId(
  interaction: Interaction | undefined,
  targetIndex: number,
): string {
  if (!interaction) return "";

  if (interaction.type === InteractionType.Lookat) {
    return (interaction as ILookat).lookats[targetIndex]?.targetId ?? "";
  }

  if (interaction.type === InteractionType.MessageBubble) {
    return (interaction as IMessageBubble).lookAt;
  }

  if (interaction.type === InteractionType.EmotionBubble) {
    return (interaction as IEmotionBubble).lookAt;
  }

  return "";
}

/** The actor that will do the looking for the picked target. */
function getOwnerActorId(
  interaction: Interaction | undefined,
  targetIndex: number,
): string | undefined {
  if (!interaction) return undefined;

  if (interaction.type === InteractionType.Lookat) {
    return (interaction as ILookat).lookats[targetIndex]?.sourceNpcId;
  }

  if (interaction.type === InteractionType.MessageBubble) {
    return (interaction as IMessageBubble).npcId;
  }

  if (interaction.type === InteractionType.EmotionBubble) {
    return (interaction as IEmotionBubble).npcId;
  }

  return undefined;
}

export default function Viewport3DSequenceLookatTargetLayer() {
  const dispatch = useDispatch();
  const currentTab = useSelector(selectSelectedTab);
  const picker = useSelector(selectSequencePositionPicker);
  const selectedElementType = useSelector(selectSelectedElementType);
  const sequence = useSelector(selectSelectedElement) as ISequence | undefined;
  const currentTake = useSelector(selectSelectedTake3D);
  const actors = useSelector(selectActors);
  const takePositions = useSelector(selectEnvironmentInteractionPoints);
  const { getSituationMetadata } = useSituationHelper();
  const { updateInteraction } = useUpdateSequenceInteraction();

  const positionContexts = useMemo(
    () =>
      getEnvironmentPositionContexts(
        takePositions ?? [],
        currentTake?.situations,
        getSituationMetadata,
      ),
    [currentTake?.situations, getSituationMetadata, takePositions],
  );

  const actorPositions = positionContexts.flatMap((context) =>
    context.actorPositions.map((position) => ({
      position,
      targetId: getSituationPositionId(context, position.id),
      takeColor: context.takeColor,
    })),
  );
  const propPositions = positionContexts.flatMap((context) =>
    context.propPositions.map((position) => ({
      position,
      targetId: getSituationPositionId(context, position.id),
      takeColor: context.takeColor,
    })),
  );
  const cameraPositions = positionContexts.flatMap((context) =>
    context.cameraPositions.map((position) => ({
      position,
      targetId: getSituationPositionId(context, position.id),
      takeColor: context.takeColor,
    })),
  );

  const activeInteraction = useMemo(() => {
    return sequence?.nodes?.find((node) => node.id === picker.nodeId)?.data
      ?.interaction as Interaction | undefined;
  }, [picker.nodeId, sequence?.nodes]);

  const isSequenceContext =
    selectedElementType === "takeSequence" ||
    (selectedElementType === "interaction" &&
      sequence?.type === InteractionType.Sequence);

  const isVisible =
    currentTab === SelectedTab.Timeline &&
    isSequenceContext &&
    picker.isOpen &&
    picker.positionType === "lookatTarget" &&
    (activeInteraction?.type === InteractionType.Lookat ||
      activeInteraction?.type === InteractionType.MessageBubble ||
      activeInteraction?.type === InteractionType.EmotionBubble);

  const targetLookatIndex = picker.targetIndex ?? 0;
  const selectedTargetId = getSelectedTargetId(
    activeInteraction,
    targetLookatIndex,
  );
  // An actor cannot look at the position it occupies itself.
  const excludedPositionId = getActorPositionIdInTake(
    currentTake,
    getOwnerActorId(activeInteraction, targetLookatIndex),
  );
  const selectableActorPositions = actorPositions.filter(
    ({ targetId }) => targetId !== excludedPositionId,
  );

  const actorByPositionId = useMemo(() => {
    return new Map(
      (currentTake?.actors ?? [])
        .map((actorState) => {
          const actor = actors.find(
            (candidate) => candidate.id === actorState.actorId,
          );
          if (!actor) return undefined;
          return [actorState.positionId, actor] as const;
        })
        .filter((entry): entry is readonly [string, Actor] => Boolean(entry)),
    );
  }, [actors, currentTake?.actors]);

  if (!isVisible || !activeInteraction) {
    return null;
  }

  const getMarkerBorderColor = (isSelected: boolean, takeColor?: RgbaColor) => {
    return isSelected ? selectedMarkerColor : takeColor;
  };

  const getMarkerBackgroundColor = (
    isSelected: boolean,
    takeColor?: RgbaColor,
  ) => {
    return isSelected ? selectedMarkerColor : undefined;
  };

  const getMarkerIconColor = (isSelected: boolean) => {
    return isSelected ? { r: 1, g: 1, b: 1, a: 1 } : undefined;
  };

  const getStrategyForActorPosition = (actor?: Actor) => {
    if (!actor) return NpcPointStrategy;
    if (actor.id === "player") return PlayerPointStrategy;
    return ActorPointStrategy;
  };

  const updateLookatTarget = (targetId: string) => {
    updateInteraction(
      picker.nodeId,
      applyPickedTargetToInteraction(
        activeInteraction,
        targetLookatIndex,
        targetId,
      ),
    );
    dispatch(closeSequencePositionPicker());
  };

  return (
    <>
      {selectableActorPositions.map(
        ({ position: actorPosition, targetId, takeColor }) => {
          const actor = actorByPositionId.get(targetId);
          const isSelected = selectedTargetId === targetId;

          return (
            <InteractionPointPreview
              key={`actor-${targetId}`}
              strategy={getStrategyForActorPosition(actor)}
              x={actorPosition.position.x}
              y={actorPosition.position.y}
              actor={actor}
              markerBorderColor={getMarkerBorderColor(isSelected, takeColor)}
              markerBackgroundColor={getMarkerBackgroundColor(
                isSelected,
                takeColor,
              )}
              markerIconColor={getMarkerIconColor(isSelected)}
              directionDegrees={actorPosition.direction?.x}
              directionColor={getMarkerBorderColor(isSelected, takeColor)}
              onClick={() => updateLookatTarget(targetId)}
            />
          );
        },
      )}
      {propPositions.map(({ position: propPosition, targetId, takeColor }) => {
        const isSelected = selectedTargetId === targetId;

        return (
          <InteractionPointPreview
            key={`prop-${targetId}`}
            strategy={PropPointStrategy}
            x={propPosition.position.x}
            y={propPosition.position.y}
            markerBorderColor={getMarkerBorderColor(isSelected, takeColor)}
            markerBackgroundColor={getMarkerBackgroundColor(
              isSelected,
              takeColor,
            )}
            markerIconColor={getMarkerIconColor(isSelected)}
            directionDegrees={propPosition.direction?.x}
            directionColor={getMarkerBorderColor(isSelected, takeColor)}
            onClick={() => updateLookatTarget(targetId)}
          />
        );
      })}
      {cameraPositions.map(
        ({ position: cameraPosition, targetId, takeColor }) => {
          const isSelected = selectedTargetId === targetId;

          return (
            <InteractionPointPreview
              key={`camera-${targetId}`}
              strategy={
                isSelected ? SelectedCameraPointStrategy : CameraPointStrategy
              }
              x={cameraPosition.position.x}
              y={cameraPosition.position.y}
              markerBorderColor={getMarkerBorderColor(isSelected, takeColor)}
              markerBackgroundColor={getMarkerBackgroundColor(
                isSelected,
                takeColor,
              )}
              markerIconColor={getMarkerIconColor(isSelected)}
              directionDegrees={cameraPosition.direction?.x}
              directionColor={getMarkerBorderColor(isSelected, takeColor)}
              onClick={() => updateLookatTarget(targetId)}
            />
          );
        },
      )}
    </>
  );
}
